import { polyfillScrollendEvent, supportsResizeObserver } from "./utils.js";

const template = document.createElement("template");

template.innerHTML = `
  <style>
    :host {
      border: none !important;
      contain: content;
      display: block;
      position: relative;
    }

    :host([truncated]) {
      display: flex;
    }

    :host([truncated]) .clamp-container {
      max-inline-size: calc(100% - 50px);
    }

    :host([expanded]) .clamp-container {
      padding-inline-end: 12px;
    }

    :host .clamp-container {
      --mask-gradient: rgba(0, 0, 0, 0) 0, rgba(0, 0, 0, 1) 48px,
        rgba(0, 0, 0, 1) calc(100% - 48px), rgba(0, 0, 0, 0) 100%;

      mask-image: linear-gradient(var(--mask-gradient)),
        linear-gradient(#000, #000);
      mask-size: calc(100% - 12px) 100%, 12px 100%;
      mask-repeat: no-repeat;
      mask-position: 0 0, 100% 0;
    }

    :host .clamp-container.is-at-start {
      --mask-gradient: rgba(0, 0, 0, 1) calc(100% - 48px), rgba(0, 0, 0, 0) 100%;
    }

    :host .clamp-container.is-at-end {
      --mask-gradient: rgba(0, 0, 0, 0) 0, rgba(0, 0, 0, 1) 48px;
    }

    :host .clamp-container.is-at-start.is-at-end {
      --mask-gradient: #000, #000;
    }

    :host([hidden]) {
      display: none;
    }

    :host [name="content"] {
      display: block;
    }

    :host [name="caption"] {
      display: block;
      max-block-size: rem(23px);
    }

    :host #expand-button {
      color: var(--color-text-invert)
    }

    :host([expanded]) [name="caption"] {
      max-block-size: none;
    }

    :host([truncated]) [name="credits"] {
      display: none;
    }

    :host([expanded]) [name="credits"] {
      display: block;
    }

    :host :is([name="expand-button"], [name="collapse-button"]) {
      display: none;
    }

    :host([truncated]) [name="expand-button"] {
      display: flex;
      flex-direction: column;
      inline-size: 100px;
      justify-content: flex-end;
    }

    :host([expanded]) [name="collapse-button"] {
      block-size: 100%;
      display: block; /* is needed to overwrite "display: none;" */
      inset-block-start: 0;
      inset-inline-end: 0;
      position: absolute;
    }
  </style>

  <slot name="collapse-button"></slot>
  <div class="clamp-container is-at-start is-at-end" part="container">
    <slot name="content"></slot>
    <slot name="caption"></slot>
    <slot name="credits"></slot>
  </div>
  <slot name="expand-button"></slot>

`;

export default class Clamp extends HTMLElement {
  #collapseButton: HTMLButtonElement;
  #expandButton: HTMLButtonElement;
  #slots: Record<string, HTMLSlotElement> = {};
  #clampContainer: HTMLElement;

  resizeObserver: any;

  get expanded() {
    return this.hasAttribute("expanded");
  }

  set expanded(value) {
    value
      ? this.setAttribute("expanded", "")
      : this.removeAttribute("expanded");
  }

  get truncated() {
    return this.hasAttribute("truncated");
  }

  set truncated(value) {
    value
      ? this.setAttribute("truncated", "")
      : this.removeAttribute("truncated");
  }

  constructor() {
    super();

    // Setup Shadow DOM
    this.attachShadow({ mode: "open" });
    this.shadowRoot!.appendChild(template.content.cloneNode(true));

    // Setup HTML elements
    this.#slots.collapse = this.shadowRoot!.querySelector(
      '[name="collapse-button"]',
    ) as HTMLSlotElement;
    this.#collapseButton =
      this.#slots.collapse.assignedElements()[0]! as HTMLButtonElement;
    this.#slots.caption = this.shadowRoot!.querySelector(
      '[name="caption"]',
    ) as HTMLSlotElement;
    this.#slots.content = this.shadowRoot!.querySelector(
      '[name="content"]',
    ) as HTMLSlotElement;
    this.#slots.credits = this.shadowRoot!.querySelector(
      '[name="credits"]',
    ) as HTMLSlotElement;
    this.#slots.expand = this.shadowRoot!.querySelector(
      '[name="expand-button"]',
    ) as HTMLSlotElement;
    this.#expandButton =
      this.#slots.expand.assignedElements()[0]! as HTMLButtonElement;
    this.#clampContainer = this.shadowRoot!.querySelector(
      ".clamp-container",
    ) as HTMLElement;
  }

  connectedCallback() {
    this.updateTruncated();
    this.#slots.expand!.addEventListener("slotchange", () => {
      this.setAttributes();
      this.registerEvents();
    });
    this.setAttributes();
    this.registerEvents();

    if (supportsResizeObserver) {
      this.resizeObserver = new ResizeObserver(this.updateTruncated);
      this.resizeObserver.observe(this);
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          this.collapse();
        }
      });
    });

    observer.observe(this);

    // Polyfills "scrollend" event in safari
    if (!("onscrollend" in window))
      polyfillScrollendEvent(this.#clampContainer);
  }

  setAttributes() {
    if (this.#expandButton instanceof HTMLElement) {
      if (!this.#expandButton.ariaExpanded) {
        this.#expandButton.ariaExpanded = "false";
      }
      if (this.id && !this.#expandButton.hasAttribute("aria-controls")) {
        this.#expandButton.setAttribute("aria-controls", this.id);
      }
    }
  }

  registerEvents() {
    this.#slots.caption!.addEventListener("click", this.expand);
    this.#expandButton.addEventListener("click", this.expand);
    this.#collapseButton.addEventListener("click", this.collapse);
  }

  updateTruncated = () => {
    // Get text inserted in #caption slot
    const captionNode = this.#slots.caption?.assignedNodes()[0];

    if (captionNode instanceof HTMLElement) {
      // Create a container with the same width as the caption node
      const container = document.createElement("div");
      container.style.visibility = "hidden";
      container.style.position = "absolute";
      container.style.inlineSize = `${captionNode.offsetWidth}px`; // Match the width
      container.style.whiteSpace = "nowrap";

      // Create the text element with matching styles
      const invisibleElement = document.createElement("div");
      const computedStyle = window.getComputedStyle(captionNode);
      invisibleElement.style.fontSize = computedStyle.fontSize;
      invisibleElement.style.fontFamily = computedStyle.fontFamily;
      invisibleElement.style.fontWeight = computedStyle.fontWeight;
      invisibleElement.style.letterSpacing = computedStyle.letterSpacing;
      invisibleElement.textContent = captionNode.textContent;

      // Add the text element to the container
      container.appendChild(invisibleElement);
      document.body.appendChild(container);

      // Use scrollWidth to determine if the content is truncated
      const isTruncated = invisibleElement.scrollWidth > container.offsetWidth;

      // Remove temporary elements and set truncated attribute
      document.body.removeChild(container);
      this.truncated = isTruncated && !this.expanded;
    } else {
      // Handle cases where the captionNode is not an HTMLElement
      this.truncated =
        this.#slots.credits!.assignedNodes()?.length > 0 && !this.expanded;
    }
  };

  expand = () => {
    if (this.expanded || !this.truncated) {
      return;
    }

    this.expanded = true;
    this.truncated = false;
    if (this.#clampContainer.scrollHeight > this.#clampContainer.clientHeight) {
      this.updateClampContainer();
      this.#clampContainer.addEventListener(
        "scrollend",
        this.updateClampContainer,
      );
    }
    this.dispatchEvent(
      new CustomEvent("change", { detail: { expanded: true } }),
    );
  };

  collapse = () => {
    if (!this.expanded) {
      return;
    }
    this.expanded = false;
    this.truncated = true;
    this.#clampContainer.classList.add("is-at-start", "is-at-end");
    this.dispatchEvent(
      new CustomEvent("change", { detail: { expanded: false } }),
    );
  };

  toggle = () => {
    this.expanded ? this.collapse() : this.expand();
  };

  updateClampContainer = () => {
    if (this.#clampContainer.scrollTop > 5) {
      this.#clampContainer.classList.remove("is-at-start");
    } else {
      this.#clampContainer.classList.add("is-at-start");
    }
    if (
      this.#clampContainer.scrollTop + this.#clampContainer.clientHeight <
      this.#clampContainer.scrollHeight - 5
    ) {
      this.#clampContainer.classList.remove("is-at-end");
    } else {
      this.#clampContainer.classList.add("is-at-end");
    }
  };

  disconnectedCallback() {
    this.#slots.content!.removeEventListener("click", this.expand);
    this.#expandButton.removeEventListener("click", this.expand);
    this.#collapseButton.removeEventListener("click", this.collapse);

    this.resizeObserver && this.resizeObserver.disconnect();
  }
}

"customElements" in window &&
  customElements.get("ws-clamp") === undefined &&
  customElements.define("ws-clamp", Clamp);

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface HTMLElementTagNameMap {
    "ws-clamp": Clamp;
  }
}
